import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, ArrowLeft, ShieldCheck } from "lucide-react";
import { nexusApi, useNexusAuth } from "@/context/NexusAuthContext";
import "@/pages/nexus/nexus.css";

export default function NexusDealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { buyer, loading } = useNexusAuth() || {};
  const [deal, setDeal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!buyer) {
      nav(`/nexus/signin?next=${encodeURIComponent(`/nexus/deals/${id}`)}`);
      return;
    }
    nexusApi.get(`/deals/${id}`)
      .then((r) => { setDeal(r.data.deal); setMessages(r.data.messages || []); })
      .catch((err) => setLoadErr(err.response?.data?.detail || "Could not load deal"));
  }, [id, buyer, loading, nav]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await nexusApi.post(`/deals/${id}/messages`, { body: body.trim() });
      setMessages((prev) => [...prev, r.data]);
      setBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally { setSending(false); }
  };

  if (loading || (!deal && !loadErr)) {
    return (
      <div className="nx-container py-24 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C9896E]" size={28} />
      </div>
    );
  }

  if (loadErr) {
    return (
      <div className="nx-container py-24 max-w-md mx-auto text-center" data-testid="deal-load-error">
        <p className="text-white/70 mb-4">{loadErr}</p>
        <Link to="/nexus/deals" className="text-[#C9896E] hover:text-white">← Back to deals</Link>
      </div>
    );
  }

  return (
    <div data-testid="nexus-deal-detail" className="nx-container py-12 max-w-3xl mx-auto">
      <Link to="/nexus/deals" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-white/50 hover:text-white mb-6" data-testid="deal-back">
        <ArrowLeft size={12} /> All deals
      </Link>

      <div className="border border-white/10 mb-6">
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="nx-eyebrow rule mb-2">Deal thread · Under NDA</div>
              <h1 className="font-serif text-2xl md:text-3xl text-white">{deal.listing_title}</h1>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{deal.reference}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                Opened {new Date(deal.created_at).toLocaleDateString("en-GB")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[#C9896E] mt-3">
            <ShieldCheck size={12} />
            <span className="font-mono uppercase tracking-[0.18em]">All messages subject to NDA {deal.nda_reference}</span>
          </div>
        </div>

        <div ref={scrollRef} className="max-h-[520px] overflow-y-auto px-6 py-5 space-y-4" data-testid="deal-messages">
          {messages.length === 0 ? (
            <div className="text-white/50 text-sm">No messages yet.</div>
          ) : messages.map((m) => <MessageBubble key={m.id} msg={m} buyerEmail={buyer.email} />)}
        </div>

        <form onSubmit={send} className="border-t border-white/10 px-6 py-4 flex gap-3 items-end" data-testid="deal-compose">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            maxLength={5000}
            placeholder="Write to the Corpora Consultants team…"
            className="nx-field flex-1 !min-h-[54px]"
            data-testid="deal-compose-textarea"
          />
          <button
            type="submit"
            className="nx-btn-primary shrink-0"
            disabled={sending || !body.trim()}
            data-testid="deal-compose-send"
          >
            {sending ? <Loader2 className="animate-spin" size={14} /> : <><Send size={14} /> Send</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({ msg, buyerEmail }) {
  const isBuyer = msg.from_role === "buyer";
  const isSystem = msg.from_role === "system";
  if (isSystem) {
    return (
      <div className="text-center text-[11px] font-mono uppercase tracking-[0.18em] text-white/40 py-2 max-w-2xl mx-auto">
        {msg.body}
      </div>
    );
  }
  return (
    <div className={`flex ${isBuyer ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] ${isBuyer ? "bg-white/[0.06] border-white/10" : "bg-[#C9896E]/10 border-[#C9896E]/25"} border px-4 py-3`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1 opacity-60">
          {isBuyer ? "You" : "Corpora Consultants"} · {new Date(msg.created_at).toLocaleString("en-GB")}
        </div>
        <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{msg.body}</div>
      </div>
    </div>
  );
}
